import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { weeks, days, dailyFramework, programIntro } from "@/data/programData";
import AnchorMark from "@/components/AnchorMark";
import { ProgramPricing } from "@/components/Pricing";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

export default function ProgramOverviewPage() {
  return (
    <div
      data-testid="program-overview-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        {/* Cover */}
        <section className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-24 md:mb-32">
          <div className="flex justify-center mb-10">
            <AnchorMark size={88} color="#15243D" />
          </div>
          <p
            className="serif text-base tracking-[0.4em] text-[#15243D] mb-8"
            style={{ fontWeight: 400 }}
          >
            ANCHOR · 28-DAY PROGRAM
          </p>
          <h1
            className="serif text-5xl md:text-7xl text-[#15243D] leading-[1.04] tracking-tight mb-10"
            style={{ fontWeight: 300 }}
          >
            Strength begins in <em className="italic text-[#C07865]">stillness</em>.
          </h1>
          <p className="text-lg md:text-xl font-light text-[#5A5D63] max-w-2xl mx-auto leading-relaxed">
            Twenty-eight short days. One quiet practice. A simple way of
            returning to yourself in the middle of real life.
          </p>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 text-sm font-light text-[#15243D]">
            {programIntro.coreFlow.map((step, i) => (
              <span key={step} className="inline-flex items-center gap-3">
                <span className="px-3 py-1.5 border border-[#15243D]/15 text-xs tracking-[0.18em] uppercase">
                  {step}
                </span>
                {i < programIntro.coreFlow.length - 1 && (
                  <ArrowRight size={14} strokeWidth={1.25} className="text-[#C07865]" />
                )}
              </span>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/program/day/1"
              data-testid="overview-begin-button"
              className="btn-primary"
            >
              Begin Day 1
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </section>

        {/* Pricing */}
        <ProgramPricing />

        {/* Daily framework */}
        <section
          className="max-w-5xl mx-auto px-6 md:px-12 mb-24 md:mb-32"
          data-testid="daily-framework"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <p className="overline mb-4">The daily move</p>
              <h2
                className="serif text-3xl md:text-4xl text-[#15243D] leading-snug"
                style={{ fontWeight: 300 }}
              >
                {dailyFramework.title}
              </h2>
              <p className="mt-6 text-sm text-[#8A8D93]">{dailyFramework.note}</p>
            </div>
            <ol className="md:col-span-7 md:col-start-6 space-y-3">
              {dailyFramework.steps.map((s, i) => (
                <li
                  key={s}
                  className="flex items-baseline gap-6 border-b border-[#15243D]/10 pb-3"
                >
                  <span
                    className="serif text-2xl text-[#8A8D93] w-8"
                    style={{ fontWeight: 300 }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base md:text-lg font-light text-[#15243D]">
                    {s}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Weeks */}
        <section
          className="max-w-7xl mx-auto px-6 md:px-12 mb-24 md:mb-32"
          data-testid="weeks-overview"
        >
          <div className="mb-12 md:mb-16">
            <p className="overline mb-4">The arc</p>
            <h2
              className="serif text-4xl md:text-5xl text-[#15243D] leading-tight max-w-2xl"
              style={{ fontWeight: 300 }}
            >
              Four weeks of <em className="italic text-[#C07865]">returning</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#15243D]/10">
            {weeks.map((w) => (
              <article
                key={w.n}
                data-testid={`week-card-${w.n}`}
                className="bg-[#F7F5F0] p-8 md:p-12"
              >
                <div className="flex items-baseline justify-between mb-6">
                  <p className="overline">Week {w.n}</p>
                  <span
                    className="serif text-sm tracking-[0.2em] text-[#8A8D93]"
                    style={{ fontWeight: 300 }}
                  >
                    Days {w.days[0]} — {w.days[w.days.length - 1]}
                  </span>
                </div>
                <h3
                  className="serif text-3xl md:text-4xl text-[#15243D] mb-2"
                  style={{ fontWeight: 300 }}
                >
                  {w.name}
                </h3>
                <p className="text-sm italic text-[#C07865] mb-6 serif" style={{ fontWeight: 300 }}>
                  {w.subtitle}
                </p>
                <p className="text-base font-light text-[#5A5D63] leading-relaxed mb-6">
                  {w.body}
                </p>
                <ul className="space-y-2 mb-8">
                  {w.focus.map((f) => (
                    <li
                      key={f}
                      className="flex items-baseline gap-3 text-sm font-light text-[#15243D]"
                    >
                      <span className="text-[#C07865] text-xs">—</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <ul className="pt-6 border-t border-[#15243D]/10 space-y-1">
                  {w.days.map((dn) => {
                    const day = days.find((d) => d.n === dn);
                    return (
                      <li key={dn}>
                        <Link
                          to={`/program/day/${dn}`}
                          data-testid={`week-${w.n}-day-${dn}`}
                          className="group flex items-baseline justify-between gap-4 py-2 text-base font-light text-[#15243D] hover:text-[#C07865] transition-colors"
                        >
                          <span className="flex items-baseline gap-4">
                            <span className="text-xs tracking-[0.2em] uppercase text-[#8A8D93] w-12">
                              Day {dn}
                            </span>
                            <span className="serif" style={{ fontWeight: 300 }}>
                              {day?.title}
                            </span>
                          </span>
                          <ArrowUpRight
                            size={14}
                            strokeWidth={1.25}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* What makes this different */}
        <section
          className="max-w-5xl mx-auto px-6 md:px-12 mb-24 md:mb-32"
          data-testid="program-what-different"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            <div>
              <p className="overline mb-4">This isn’t</p>
              <ul className="space-y-3">
                {[
                  "Meditation as escape",
                  "Productivity hacks",
                  "Fixing your thoughts",
                ].map((i) => (
                  <li
                    key={i}
                    className="text-lg font-light text-[#5A5D63] line-through decoration-[#15243D]/20"
                  >
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="overline mb-4">This is</p>
              <ul className="space-y-3">
                {[
                  "Applied philosophy",
                  "Nervous system regulation in real time",
                  "Identity shift — the grounded one in the room",
                ].map((i) => (
                  <li
                    key={i}
                    className="text-lg font-light text-[#15243D] flex items-baseline gap-3"
                  >
                    <span className="text-[#C07865] text-xs">—</span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Final */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="flex justify-center mb-10">
            <AnchorMark size={50} color="#15243D" className="opacity-50" />
          </div>
          <p
            className="serif italic text-2xl md:text-3xl text-[#15243D] leading-snug"
            style={{ fontWeight: 300 }}
          >
            {programIntro.finalLine}
          </p>
          <div className="mt-12">
            <Link
              to="/program/day/1"
              data-testid="overview-begin-button-bottom"
              className="btn-primary"
            >
              Begin Day 1
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
